import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../lib/config';
import {
  UsersIcon,
  BriefcaseIcon,
  CurrencyYenIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';

// API_BASE already points to "/api"
const API = API_BASE;

const DashboardOverview = ({ onNavigate, badgeCounts }) => {
  useEffect(() => {
    console.log("DashboardOverview Loaded - CLEAN VERSION");
  }, []);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalJobSeekers: 0,
    totalEmployers: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    recentUsers30d: 0,
    recentJobs30d: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [counts, setCounts] = useState({
    new_users: 0,
    pending_reports: 0,
    pending_job_approvals: 0,
    payment_errors: 0,
    new_applications: 0,
    pending_identity_verifications: 0,
  });
  const [systemStatus, setSystemStatus] = useState({
    web: 'active',
    db: 'active',
    payment: 'active'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    const id = setInterval(() => {
      fetchCounts();
    }, 15000);
    fetchCounts();
    return () => clearInterval(id);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      // Try to fetch recent activities from backend
      try {
        const act = await axios.get(`${API}/admin/activities/recent`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(act.data?.items)) {
          setRecentActivities(act.data.items);
        }
      } catch (e) {
        // ignore and fall back below
      }
      // Fetch identity stats to reflect pending KYC count early
      try {
        const statsRes = await axios.get(`${API}/admin/users/identity-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const p = statsRes.data?.pending_verifications || 0;
        setCounts(prev => ({ ...prev, pending_identity_verifications: p }));
      } catch (_) {
        // ignore
      }
      // Fetch system status
      try {
        const sysRes = await axios.get(`${API}/admin/system/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSystemStatus(sysRes.data);
      } catch (_) {
        setSystemStatus({ web: 'active', db: 'error', payment: 'unknown' });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Fallback to demo data
      // Fallback to zero data
      setStats({
        totalUsers: 0,
        totalJobSeekers: 0,
        totalEmployers: 0,
        totalJobs: 0,
        activeJobs: 0,
        totalApplications: 0,
        recentUsers30d: 0,
        recentJobs30d: 0
      });
    } finally {
      setLoading(false);
    }
    // No hardcoded recent activities
  };

  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      // Best-effort: metrics may not exist in all envs
      try {
        const r = await axios.get(`${API}/admin/metrics/counts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCounts(prev => ({ ...prev, ...(r.data || {}) }));
      } catch (_) { }
      // Always refresh identity pending count
      try {
        const statsRes = await axios.get(`${API}/admin/users/identity-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const p = statsRes.data?.pending_verifications || 0;
        setCounts(prev => ({ ...prev, pending_identity_verifications: p }));
      } catch (_) { }
    } catch (e) {
      // ignore polling errors
    }
  };

  const statCards = [
    {
      title: '総ユーザー数',
      value: stats.total_users?.toLocaleString() || '0',
      icon: <UsersIcon className="w-6 h-6" />,
      color: 'blue'
    },
    {
      title: '求職者',
      value: stats.total_job_seekers?.toLocaleString() || '0',
      icon: <UsersIcon className="w-6 h-6" />,
      color: 'green'
    },
    {
      title: '事業者',
      value: stats.total_employers?.toLocaleString() || '0',
      icon: <BriefcaseIcon className="w-6 h-6" />,
      color: 'purple'
    },
    {
      title: 'アクティブ求人',
      value: stats.active_jobs?.toLocaleString() || '0',
      icon: <BriefcaseIcon className="w-6 h-6" />,
      color: 'orange'
    },
    {
      title: '総応募数',
      value: stats.total_applications?.toLocaleString() || '0',
      icon: <ChartBarIcon className="w-6 h-6" />,
      color: 'indigo'
    },
    {
      title: '月間売上',
      value: stats.monthly_revenue?.toLocaleString() || '0',
      icon: <CurrencyYenIcon className="w-6 h-6" />,
      color: 'emerald'
    },
    {
      title: '新規登録（30日）',
      value: stats.recent_users_30d?.toLocaleString() || '0',
      icon: <ExclamationTriangleIcon className="w-6 h-6" />,
      color: 'red'
    }
  ];

  const getCardBgColor = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      green: 'bg-green-50 border-green-200',
      purple: 'bg-purple-50 border-purple-200',
      orange: 'bg-orange-50 border-orange-200',
      indigo: 'bg-indigo-50 border-indigo-200',
      emerald: 'bg-emerald-50 border-emerald-200',
      red: 'bg-red-50 border-red-200'
    };
    return colors[color] || colors.blue;
  };

  const getCardIconColor = (color) => {
    const colors = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600',
      emerald: 'text-emerald-600',
      red: 'text-red-600'
    };
    return colors[color] || colors.blue;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_register':
        return <UsersIcon className="w-4 h-4" />;
      case 'job_post':
        return <BriefcaseIcon className="w-4 h-4" />;
      case 'report':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'payment':
        return <CurrencyYenIcon className="w-4 h-4" />;
      case 'application':
        return <ChartBarIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getActivityColor = (severity) => {
    switch (severity) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="bg-white border rounded-lg p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
                <div className="h-6 w-6 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))
        ) : (
          statCards.map((card, index) => (
            <div
              key={index}
              className={`${getCardBgColor(card.color)} border rounded-lg p-6 transition-shadow hover:shadow-md`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                  {card.change && card.changeType && (
                    <div className="flex items-center mt-2">
                      {card.changeType === 'positive' ? (
                        <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
                      )}
                      <span className={`text-sm font-medium ${card.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                        }`}>
                        {card.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">先月比</span>
                    </div>
                  )}
                </div>
                <div className={`${getCardIconColor(card.color)}`}>
                  {card.icon}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 最近のアクティビティ */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">最近のアクティビティ</h3>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`p-2 rounded-full ${getActivityColor(activity.severity)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              すべて表示
            </button>
          </div>
        </div>

        {/* クイックアクション */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">クイックアクション</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <button
              onClick={() => onNavigate && onNavigate('identity-verification')}
              className="bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-lg p-4 transition-colors relative"
            >
              <IdentificationIcon className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-yellow-900 flex items-center justify-center">
                本人確認
                {(badgeCounts?.['identity-verification'] > 0 || counts.pending_identity_verifications > 0) && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1 h-5 text-xs font-semibold text-white bg-yellow-600 rounded-full">
                    {badgeCounts?.['identity-verification'] || counts.pending_identity_verifications}
                  </span>
                )}
              </p>
              <p className="text-xs text-yellow-600">審査待ち</p>
            </button>
            <button
              onClick={() => onNavigate && onNavigate('users')}
              className="bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-4 transition-colors"
            >
              <UsersIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-blue-900 flex items-center justify-center">
                新規ユーザー
                {(badgeCounts?.['users'] > 0 || counts.new_users > 0) && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1 h-5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                    {badgeCounts?.['users'] || counts.new_users}
                  </span>
                )}
              </p>
              <p className="text-xs text-blue-600">確認</p>
            </button>
            <button
              onClick={() => onNavigate && onNavigate('reports')}
              className="bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg p-4 transition-colors"
            >
              <ExclamationTriangleIcon className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-red-900 flex items-center justify-center">
                通報
                {(badgeCounts?.['reports'] > 0 || counts.pending_reports > 0) && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
                    {badgeCounts?.['reports'] || counts.pending_reports}
                  </span>
                )}
              </p>
              <p className="text-xs text-red-600">対応待ち</p>
            </button>
            <button
              onClick={() => onNavigate && onNavigate('jobs')}
              className="bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-4 transition-colors"
            >
              <BriefcaseIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-900 flex items-center justify-center">
                求人
                {(badgeCounts?.['jobs'] > 0 || counts.pending_job_approvals > 0) && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1 h-5 text-xs font-semibold text-white bg-green-600 rounded-full">
                    {badgeCounts?.['jobs'] || counts.pending_job_approvals}
                  </span>
                )}
              </p>
              <p className="text-xs text-green-600">承認待ち</p>
            </button>
            <button
              onClick={() => onNavigate && onNavigate('payments')}
              className="bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg p-4 transition-colors"
            >
              <CurrencyYenIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-purple-900 flex items-center justify-center">
                決済エラー
                {(badgeCounts?.['payments'] > 0 || counts.payment_errors > 0) && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1 h-5 text-xs font-semibold text-white bg-purple-600 rounded-full">
                    {badgeCounts?.['payments'] || counts.payment_errors}
                  </span>
                )}
              </p>
              <p className="text-xs text-purple-600">対応必要</p>
            </button>
            <button
              onClick={() => onNavigate && onNavigate('wallet')}
              className="bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg p-4 transition-colors"
            >
              <ChartBarIcon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-indigo-900 flex items-center justify-center">
                出金申請
                {(badgeCounts?.['wallet'] > 0) && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1 h-5 text-xs font-semibold text-white bg-indigo-600 rounded-full">
                    {badgeCounts?.['wallet']}
                  </span>
                )}
              </p>
              <p className="text-xs text-indigo-600">申請確認</p>
            </button>
          </div>
        </div>
      </div>

      {/* システム状況 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">システム状況</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${systemStatus.web === 'active' ? 'bg-green-100' : 'bg-red-100'}`}>
              <div className={`w-3 h-3 rounded-full ${systemStatus.web === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
            </div>
            <p className="text-sm font-medium text-gray-900">Webサーバー</p>
            <p className={`text-xs ${systemStatus.web === 'active' ? 'text-green-600' : 'text-red-600'}`}>
              {systemStatus.web === 'active' ? '正常稼働' : '異常あり'}
            </p>
          </div>
          <div className="text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${systemStatus.db === 'active' ? 'bg-green-100' : 'bg-red-100'}`}>
              <div className={`w-3 h-3 rounded-full ${systemStatus.db === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
            </div>
            <p className="text-sm font-medium text-gray-900">データベース</p>
            <p className={`text-xs ${systemStatus.db === 'active' ? 'text-green-600' : 'text-red-600'}`}>
              {systemStatus.db === 'active' ? '正常稼働' : '接続エラー'}
            </p>
          </div>
          <div className="text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${systemStatus.payment === 'active' ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <div className={`w-3 h-3 rounded-full ${systemStatus.payment === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            </div>
            <p className="text-sm font-medium text-gray-900">決済システム</p>
            <p className={`text-xs ${systemStatus.payment === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
              {systemStatus.payment === 'active' ? '正常稼働' : 'メンテナンス中/未設定'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;